import * as _4 from 'lodash4';
import { PlatformUtility } from 'utility-ts-platform';

declare type ai_element = HTMLElement & { invoke: (config: object) => void };
const ngHILTemplate = `
<hi-button id="mobileGoBackBtn" aria-label="Go Back" aesthetic="icon" ng-click="$ctrl.goBack()" kind="state" layout-icon="layout-icon" size="large">
  <hi-icon aria-hidden="true" size="large" kind="chevron_left"></hi-icon>
</hi-button>
<hi-link ng-on-trigger-ended="$ctrl.goBack()" href="#" id="goBackLink">
    <hi-icon kind="chevron_left" l-size="1" size="small"></hi-icon>
    <span class="pasx">Go back</span>
</hi-link>
<div class="{{$ctrl.containerClass}} clr-white-bkg mtm" scroll-header>
  <ai-view id="aiView{{$ctrl.uniqueID}}">
  </ai-view>
</div>
`;

interface IAppConfigModel {
  getConfigProperty(s: string): string;
}

interface IAccountModel {
  student_nk: string;
  firstName: string;
}

interface IReportConfigEntry {
  is_active: boolean;
  last_enabled_dt: string;
  name: string;
  nk: string;
}

class NgHilController {
  private uri: string = '';
  private containerClass = '';
  private uniqueID: string = _4.uniqueId();
  private reportConfig: IReportConfigEntry[] = [];
  private newerThanSeven: string;
  private newerThanThirty: string;
  private newerThanNinety: string;
  constructor(
    private $element: ng.IRootElementService,
    private $injector: { get: (key: string) => unknown },
    private $http: ng.IHttpService,
    private log: any,
    private accountModel: IAccountModel,
    private appConfigModel: IAppConfigModel,
    private NetworkUrlConfiguration: any
  ) {
    this.reportConfig = JSON.parse(localStorage.getItem('eab.navigate.report_config') || '{}');
  }

  $onInit() {
    this.fetchandInvokeScript(this.uri);
    this.containerClass = PlatformUtility.isMobile() ? `app-content--mobile plm prm` : `app-content--desktop plm prm`;
    this.setComparator();
    this.newerThanSeven = moment(new Date().getTime() - 7 * 1000 * 3600 * 24).format();
    this.newerThanThirty = moment(new Date().getTime() - 30 * 1000 * 3600 * 24).format();
    this.newerThanNinety = moment(new Date().getTime() - 90 * 1000 * 3600 * 24).format();
  }

  goBack() {
    const previousURI = this.goBackURI();
    window['hosting']['kernel'].processes.history.goBackCommit();
    window.location.assign(previousURI);
  }

  goBackURI() {
    return window['hosting']['kernel'].processes.history.goBackURI();
  }

  insertConfigurationIntoHIL(hil: Record<string, any>) {
    const STC_CONFIG_KEY = 'core-campus_url';

    hil['configuration'] = Object.assign(hil['configuration'] || {}, {
      'core-campus_url': this.appConfigModel.getConfigProperty(STC_CONFIG_KEY),
      student_nk: this.accountModel.student_nk,
      app_url: this.NetworkUrlConfiguration.configure().prependUrl || '',
      firstName: this.accountModel.firstName
    });

    hil['constants'] = {
      ...(hil['constants'] || {}),
      '@SGDURI': this.NetworkUrlConfiguration.configure().prependUrl || '',
      '@STCURI': this.appConfigModel.getConfigProperty(STC_CONFIG_KEY) || '',
      '@student_nk': this.accountModel.student_nk || '',
      '@appointment_summary_enabled_date': _4.get(
        _4.filter(this.reportConfig, ['nk', 'APPOINTMENT_SUMMARIES'])[0],
        'last_enabled_dt',
        ''
      ),
      '@notes_enabled_date': _4.get(_4.filter(this.reportConfig, ['nk', 'NOTES'])[0], 'last_enabled_dt', ''),
      '@progress_report_enabled_date': _4.get(
        _4.filter(this.reportConfig, ['nk', 'PROGRESS_REPORTS'])[0],
        'last_enabled_dt',
        ''
      )
    };

    return hil;
  }

  fetchandInvokeScript(uri: string) {
    this.$http.get(uri, { cache: false }).then(
      (response: any) => {
        const updatedHIL = this.insertConfigurationIntoHIL(response.data);
        this.invokeView(updatedHIL);
      },
      (err: Error) => {
        this.log.error(err);
      }
    );
  }

  invokeView(script: object) {
    const aiView = document.querySelector(`#aiView${this.uniqueID}`) as ai_element;
    (window as any).hiReady(() => {
      if (aiView) {
        aiView.invoke(script);
      }
    });
  }

  hasComparator(comparator: string) {
    const visitorC = (window as any).HIP.visitor.query({ render_type: comparator, entity: 'comparator' });
    return _4.isEqual(visitorC.render_type, comparator);
  }

  setComparator() {
    if (_4.includes(this.uri, 'appt_summary.hil') && !this.hasComparator('sgd_reports_appt_summ_comparator')) {
      (window as any).HIP.visitor.add({
        entity: 'comparator',
        render_type: 'sgd_reports_appt_summ_comparator',
        compare: (left: any, right: any) => {
          const inSupportedFormat = 'object' === typeof left && 'string' === typeof right;
          if (!inSupportedFormat) {
            return left == right;
          }
          switch (right) {
            case 'all':
              return true;
            case 'unread':
            case 'flagged':
              return left.status === right;
            case 'seven':
              return left.created_at > this.newerThanSeven;
            case 'thirty':
              return left.created_at > this.newerThanThirty;
            case 'ninety':
              return left.created_at > this.newerThanNinety;
            case 'old':
              return left.created_at < this.newerThanNinety || left.created_at === this.newerThanNinety;
            default:
              return _4.lowerCase(left.care_unit) === right;
          }
        }
      });
    } else if (_4.includes(this.uri, 'notes.hil') && !this.hasComparator('sgd_reports_comparator')) {
      (window as any).HIP.visitor.add({
        entity: 'comparator',
        render_type: 'sgd_reports_comparator',
        compare: (left: any, right: any) => {
          const inSupportedFormat = 'object' === typeof left && 'string' === typeof right;
          if (!inSupportedFormat) {
            return left == right;
          }
          switch (right) {
            case 'all':
              return true;
            case 'unread':
            case 'flagged':
              return left.status === right;
            case 'seven':
              return left.created_at > this.newerThanSeven;
            case 'thirty':
              return left.created_at > this.newerThanThirty;
            case 'ninety':
              return left.created_at > this.newerThanNinety;
            case 'old':
              return left.created_at < this.newerThanNinety || left.created_at === this.newerThanNinety;
            case 'no note reason':
              return _4.isEmpty(left.note_reasons);
            default:
              return _4.map(left.note_reasons, _4.lowerCase).includes(right);
          }
        }
      });
    } else if (
      _4.includes(this.uri, 'progress_report.hil') &&
      !this.hasComparator('sgd_reports_prog_rprt_comparator')
    ) {
      (window as any).HIP.visitor.add({
        entity: 'comparator',
        render_type: 'sgd_reports_prog_rprt_comparator',
        compare: (left: any, right: any) => {
          const inSupportedFormat = 'object' === typeof left && 'string' === typeof right;
          if (!inSupportedFormat) {
            return left == right;
          }
          switch (right) {
            case 'all':
              return true;
            case 'unread':
            case 'flagged':
              return left.status === right;
            case 'seven':
              return left.created_at > this.newerThanSeven;
            case 'thirty':
              return left.created_at > this.newerThanThirty;
            case 'ninety':
              return left.created_at > this.newerThanNinety;
            case 'old':
              return left.created_at < this.newerThanNinety || left.created_at === this.newerThanNinety;
            default:
              return _4.lowerCase(left.course_name) === right;
          }
        }
      });
    } else if (_4.includes(this.uri, 'self_alerts.hil') && !this.hasComparator('sgd_reports_self_alerts_comparator')) {
      (window as any).HIP.visitor.add({
        entity: 'comparator',
        render_type: 'sgd_reports_self_alerts_comparator',
        compare: (left: any, right: any) => {
          const inSupportedFormat = 'object' === typeof left && 'string' === typeof right;
          if (!inSupportedFormat) {
            return left == right;
          }
          switch (right) {
            case 'all':
              return true;
            case 'seven':
              return left.created_at > this.newerThanSeven;
            case 'thirty':
              return left.created_at > this.newerThanThirty;
            case 'ninety':
              return left.created_at > this.newerThanNinety;
            case 'old':
              return left.created_at < this.newerThanNinety || left.created_at === this.newerThanNinety;
            default:
              return true;
          }
        }
      });
    }
  }
}

export class NgHilComponent implements angular.IComponentOptions {
  public template: string = ngHILTemplate;
  public controllerAs: string = '$ctrl';
  public controller = NgHilController;
  public bindings: any = {
    uri: '@',
    title: '@'
  };
}

angular.module('capability-ng-reports').component('summaryNgHilComponent', new NgHilComponent());



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-reports/src/components/summary-ng-hil-component/summary-ng-hil-component.ts